import type { EventDetails } from '~entities/events';

import type { Medical } from './medical';

export interface Student {
  __v: number;
  _id: string;
  createdAt: string; // ISO date string
  dob: string; // ISO date string
  enrollments?: Array<Enrollment>;
  events?: Array<Event>;
  gender: Gender | '';
  medical: Medical;
  mobileNo: string;
  name: string;
  firstName: string;
  lastName: string;
  studentName?: string;
  parentId: string;
  studentEmail: string;
  studioId: string;
  tShirtSize: string;
  transportation: string;
  updatedAt: string; // ISO date string
  familyName: string;
  profilePicUrl: string;
  photoOptOut?: boolean;
  /** Published recitals the student takes part in (attendance roster only). */
  recitals?: Array<RosterRecital>;
  attendance: Array<Attendance | null>;
  trialEnrollments?: Array<TrialEnrollment>;
}

export interface RosterRecital {
  recitalId: string;
  name: string;
}

export interface Enrollment {
  enrollmentId?: ClassDetail;
  absences: number;
  skills: number;
  enrolledDate: string;
  subscriptionId: string;
  subscriptionStatus:
    | 'active'
    | 'inactive'
    | 'term-ended'
    | 'class-transferred'
    | 'paused'
    | 'scheduled';
}

export enum Gender {
  Male = 'Male',
  Female = 'Female',
  NonBinary = 'Non-binary',
  PreferNotToSay = 'Prefer not to say',
}

export interface Event {
  eventId?: EventDetails;
  absences: number;
  skills: number;
  /** Enrolled date — the field the backend actually stores on student.events. */
  eventDate?: string;
  enrolledDate: string;
  subscriptionId: string;
  subscriptionStatus: string;
}

export interface ClassDetail {
  _id: string;
  title: string;
  studio: string;
  students: Array<unknown>;
  location: string;
  room: string;
  instructor: Array<Instructor>;
  days: Array<string>;
  startTime: string;
  endTime: string;
  duration: number;
  registrationStartDate: string;
  registrationEndDate: string;
  startDate: string;
  endDate: string;
  tuitionFee: number;
  maxSize: number;
  maxWait: number;
  allowOnlineRegistration: boolean;
  allowPortalEnrollment: boolean;
  allowTrialEnrollment: boolean;
  displayOnWebsite: boolean;
  tuitionBillingMethod: string;
  tuitionBillingCycle: string;
  tuitionDiscountRule: string;
  prorateTuition: boolean;
  registrationFee: boolean;
  registrationFeeAmount: number;
  tags: Array<string>;
  group: Array<string>;
  policyGroup: Array<string>;
  description: string;
  mainTeacher: string;
  subTeacher: string;
  startAge: number;
  endAge: number;
  createdAt: string;
  updatedAt: string;
  __v: number;
  /** When present, class is archived (hidden from default listings). */
  isArchive?: boolean;
  session?: {
    endDate?: string;
  };
}

export interface Instructor {
  fieldName: string;
  _id: string;
}

export interface Attendance {
  attendanceId: AttendanceID | null;
  date: string;
}

export interface AttendanceID {
  _id: string;
  date: string;
  class: string;
  studio: string;
  __v: number;
  attendanceRecord: PresentRecord | AbsentRecord;
  isPresent: boolean;
  statusDetail: string;
  createdAt: string;
  updatedAt: string;
}

export interface PresentRecord {
  student: string;
  isPresent: true;
  statusDetail: 'on-time' | 'late-arrival' | 'left-early';
}

export interface AbsentRecord {
  student: string;
  isPresent: false;
  statusDetail:
    'sick-leave' | 'out-of-town' | 'family-event' | 'uninformed-absence';
}

export interface TrialEnrollment {
  _id: string;
  className?: string;
  startTime?: string;
  endTime?: string;
  appointmentStatus?: string;
  classId?: {
    _id: string;
    title: string;
    days?: Array<string>;
    instructor?: Array<{ _id: string; fieldName: string }>;
  };
}
